import React from 'react';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const QUOTA_LABELS = {
  analyst:    'analyst profiles',
  prediction: 'predictions',
  symbol:     'symbols',
};

/**
 * Inline banner shown at the bottom of a paginated list when list_limit_reached is true.
 *
 * Props:
 *   limit — the cap that was applied (number), from the list API response
 *   type  — 'analyst' | 'prediction' | 'symbol' — which quota to show from me API
 */
export default function ListLimitBanner({ limit, type }) {
  const { user } = useAuth();
  const isAnon = !user;
  const tier = user?.tier ?? 'free';
  const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);

  const quota = type ? user?.quota?.[type] : null;
  const quotaLabel = QUOTA_LABELS[type] ?? type;
  const used = quota?.used_today ?? 0;
  const daily = quota?.daily_limit ?? null;

  return (
    <div className="mt-4 flex items-center gap-4 px-5 py-4 bg-gray-50 border border-gray-200 rounded-xl">
      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
        <Lock size={14} className="text-indigo-600" />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800">
          Showing top {limit} results
        </p>
        <p className="text-xs text-gray-500 mt-0.5">
          {isAnon
            ? 'Sign in to see the full list'
            : `You're on the ${tierLabel} plan · Upgrade for unlimited access`}
        </p>
        {!isAnon && quota && daily !== null && (
          <p className="text-xs text-gray-400 mt-0.5">
            {used} of {daily} {quotaLabel} viewed today
          </p>
        )}
      </div>

      {isAnon ? (
        <Link
          to="/login"
          className="flex-shrink-0 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold transition-colors"
        >
          Sign in
        </Link>
      ) : (
        <Link
          to="/upgrade"
          className="flex-shrink-0 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold transition-colors"
        >
          Upgrade
        </Link>
      )}
    </div>
  );
}
